// TransformationValidator - Handles validation of transformation results
export class TransformationValidator {
  constructor() {
    this.validationStats = {
      totalValidations: 0,
      validResponses: 0,
      invalidResponses: 0,
      warningsGenerated: 0,
      errorsFound: 0
    };
    
    this.validationRules = {
      required: ['category_id', 'control_item_id', 'check_id', 'field_label', 'selected_points'],
      numeric: ['category_id', 'control_item_id', 'check_id', 'selected_points', 'quantity'],
      optional: ['field_builder_id', 'remark', 'production_phase', 'image_1_url', 'image_2_url', 'image_3_url'],
      maxLengths: {
        field_label: 255,
        category_title: 100,
        remark: 1000,
        production_phase: 50
      }
    };
  }

  // Validate single field response structure
  validateFieldResponse(fieldResponse) {
    this.validationStats.totalValidations++;
    
    const errors = [];
    const warnings = [];
    const info = [];
    
    try {
      // Required fields validation
      this.validationRules.required.forEach(field => {
        if (fieldResponse[field] === undefined || fieldResponse[field] === null) {
          errors.push(`Missing required field: ${field}`);
        } else if (typeof fieldResponse[field] === 'string' && fieldResponse[field].trim() === '') {
          errors.push(`Required field '${field}' is empty`);
        }
      });
      
      // Numeric fields validation
      this.validationRules.numeric.forEach(field => {
        if (fieldResponse[field] !== undefined && fieldResponse[field] !== null) {
          if (typeof fieldResponse[field] !== 'number') {
            if (isNaN(Number(fieldResponse[field]))) {
              errors.push(`Field '${field}' must be numeric, got: ${typeof fieldResponse[field]}`);
            } else {
              warnings.push(`Field '${field}' should be number, got string that can be converted`);
            }
          }
        }
      });
      
      // Range validations
      if (fieldResponse.selected_points !== undefined) {
        if (fieldResponse.selected_points < 0) {
          warnings.push('selected_points is negative - unusual for deduction points');
        }
        if (fieldResponse.selected_points > 100) {
          warnings.push('selected_points is very high (>100) - verify if correct');
        }
      }
      
      if (fieldResponse.quantity !== undefined && fieldResponse.quantity < 0) {
        errors.push('quantity cannot be negative');
      }
      
      // String length validations
      Object.keys(this.validationRules.maxLengths).forEach(field => {
        if (fieldResponse[field] && typeof fieldResponse[field] === 'string') {
          const maxLength = this.validationRules.maxLengths[field];
          if (fieldResponse[field].length > maxLength) {
            warnings.push(`Field '${field}' exceeds maximum length of ${maxLength} characters`);
          }
        }
      });
      
      // ID validations
      ['category_id', 'control_item_id', 'check_id'].forEach(field => {
        if (fieldResponse[field] !== undefined && fieldResponse[field] <= 0) {
          errors.push(`Field '${field}' must be a positive integer`);
        }
      });
      
      // Boolean validations
      if (fieldResponse.is_fixed_before_shipment !== undefined && 
          typeof fieldResponse.is_fixed_before_shipment !== 'boolean') {
        warnings.push('is_fixed_before_shipment should be boolean');
      }
      
      // URL validations
      ['image_1_url', 'image_2_url', 'image_3_url'].forEach(field => {
        if (fieldResponse[field] && typeof fieldResponse[field] === 'string') {
          if (fieldResponse[field].trim() !== '' && !this.isValidUrl(fieldResponse[field])) {
            warnings.push(`Field '${field}' does not appear to be a valid URL`);
          }
        }
      });
      
      // Additional data validation
      if (fieldResponse.additional_data) {
        if (typeof fieldResponse.additional_data !== 'object') {
          warnings.push('additional_data should be an object');
        } else {
          // Validate additional_data contents
          if (fieldResponse.additional_data.isChecked !== undefined && 
              typeof fieldResponse.additional_data.isChecked !== 'boolean') {
            warnings.push('additional_data.isChecked should be boolean');
          }
          
          if (fieldResponse.additional_data.deductionPoints !== undefined && 
              typeof fieldResponse.additional_data.deductionPoints !== 'number') {
            warnings.push('additional_data.deductionPoints should be number');
          }
        }
      }
      
      // Success indicators
      if (errors.length === 0) {
        this.validationStats.validResponses++;
        
        if (fieldResponse.additional_data?.isIndependent) {
          info.push('Field response marked as independent audit');
        }
        
        if (fieldResponse.remark && fieldResponse.remark.trim() !== '') {
          info.push('Field response includes detailed remarks');
        }
        
        if (fieldResponse.image_1_url || fieldResponse.image_2_url || fieldResponse.image_3_url) {
          info.push('Field response includes image references');
        }
      } else {
        this.validationStats.invalidResponses++;
      }
      
      this.validationStats.errorsFound += errors.length;
      this.validationStats.warningsGenerated += warnings.length;
      
      return {
        isValid: errors.length === 0,
        quality: this.calculateQualityScore(fieldResponse, errors, warnings),
        errors,
        warnings,
        info,
        isIndependent: fieldResponse.additional_data?.isIndependent || false
      };
      
    } catch (error) {
      console.error('❌ Error during field response validation:', error);
      this.validationStats.invalidResponses++;
      this.validationStats.errorsFound++;
      
      return {
        isValid: false,
        quality: 0,
        errors: [`Validation error: ${error.message}`],
        warnings: [],
        info: [],
        isIndependent: false
      };
    }
  }

  // Calculate quality score for field response
  calculateQualityScore(fieldResponse, errors, warnings) {
    let score = 100;
    
    // Deduct points for errors (critical issues)
    score -= errors.length * 25;
    
    // Deduct points for warnings (minor issues)
    score -= warnings.length * 5;
    
    // Bonus points for rich data
    if (fieldResponse.remark && fieldResponse.remark.trim().length > 10) {
      score += 5; // Detailed remarks
    }
    
    if (fieldResponse.production_phase && fieldResponse.production_phase.trim() !== '') {
      score += 3; // Production phase specified
    }
    
    if (fieldResponse.image_1_url || fieldResponse.image_2_url || fieldResponse.image_3_url) {
      score += 7; // Has image references
    }
    
    if (fieldResponse.additional_data && Object.keys(fieldResponse.additional_data).length > 2) {
      score += 5; // Rich additional data
    }
    
    return Math.max(0, Math.min(100, score));
  }

  // Batch validate all field responses
  validateAllFieldResponses(fieldResponses) {
    console.log(`🔍 Starting validation of ${fieldResponses.length} field responses...`);
    
    const startTime = Date.now();
    const results = fieldResponses.map((response, index) => ({
      index,
      validation: this.validateFieldResponse(response)
    }));
    
    const hasErrors = results.some(result => !result.validation.isValid);
    const errorCount = results.filter(result => !result.validation.isValid).length;
    const warningCount = results.reduce((count, result) => 
      count + (result.validation.warnings?.length || 0), 0);
    
    // Calculate aggregate statistics
    const qualityScores = results.map(r => r.validation.quality);
    const averageQuality = qualityScores.length > 0 ? 
      qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length : 0;
    
    const duration = Date.now() - startTime;
    
    console.log(`✅ Validation completed in ${duration}ms`);
    console.log(`📊 Results: ${fieldResponses.length - errorCount}/${fieldResponses.length} valid, ${warningCount} warnings`);
    
    return {
      isValid: !hasErrors,
      totalResponses: fieldResponses.length,
      validCount: fieldResponses.length - errorCount,
      errorCount,
      warningCount,
      averageQuality: Math.round(averageQuality),
      duration,
      details: results.filter(result => !result.validation.isValid),
      summary: {
        highQuality: qualityScores.filter(score => score >= 80).length,
        mediumQuality: qualityScores.filter(score => score >= 60 && score < 80).length,
        lowQuality: qualityScores.filter(score => score < 60).length
      },
      isIndependent: true
    };
  }

  // Advanced validation with context
  validateWithContext(fieldResponses, context = {}) {
    const {
      strictMode = false,
      allowEmptyRemarks = true,
      requireImages = false,
      customRules = []
    } = context;
    
    console.log(`🔍 Starting advanced validation with context...`);
    console.log(`Context: strict=${strictMode}, allowEmptyRemarks=${allowEmptyRemarks}, requireImages=${requireImages}`);
    
    const results = fieldResponses.map((response, index) => {
      const baseValidation = this.validateFieldResponse(response);
      
      // Apply context-specific rules
      const contextErrors = [];
      const contextWarnings = [];
      
      if (strictMode) {
        // In strict mode, warnings become errors
        contextErrors.push(...baseValidation.warnings.map(w => `STRICT: ${w}`));
        baseValidation.warnings = [];
      }
      
      if (!allowEmptyRemarks && (!response.remark || response.remark.trim() === '')) {
        contextErrors.push('Remarks are required in this context');
      }
      
      if (requireImages) {
        const hasImages = response.image_1_url || response.image_2_url || response.image_3_url;
        if (!hasImages) {
          contextErrors.push('At least one image is required in this context');
        }
      }
      
      // Apply custom rules
      customRules.forEach(rule => {
        try {
          const ruleResult = rule(response, index);
          if (ruleResult.errors) {
            contextErrors.push(...ruleResult.errors);
          }
          if (ruleResult.warnings) {
            contextWarnings.push(...ruleResult.warnings);
          }
        } catch (error) {
          contextWarnings.push(`Custom rule error: ${error.message}`);
        }
      });
      
      return {
        index,
        validation: {
          ...baseValidation,
          errors: [...baseValidation.errors, ...contextErrors],
          warnings: [...baseValidation.warnings, ...contextWarnings],
          isValid: baseValidation.isValid && contextErrors.length === 0,
          contextApplied: true
        }
      };
    });
    
    // Recalculate summary with context
    const errorCount = results.filter(r => !r.validation.isValid).length;
    const warningCount = results.reduce((count, r) => count + r.validation.warnings.length, 0);
    
    return {
      isValid: errorCount === 0,
      totalResponses: fieldResponses.length,
      validCount: fieldResponses.length - errorCount,
      errorCount,
      warningCount,
      context,
      results: results.filter(r => !r.validation.isValid), // Only invalid ones
      contextApplied: true
    };
  }

  // Validate transformation integrity
  validateTransformationIntegrity(originalData, transformedData) {
    console.log('🔍 Validating transformation integrity...');
    
    const integrity = {
      originalChecks: 0,
      transformedResponses: transformedData.length,
      missingTransformations: 0,
      unexpectedTransformations: 0,
      dataConsistency: true,
      issues: []
    };
    
    // Count original checks
    Object.keys(originalData).forEach(categoryId => {
      const categoryData = originalData[categoryId];
      
      if (categoryData?.controlItems) {
        Object.keys(categoryData.controlItems).forEach(controlItemId => {
          const controlItemData = categoryData.controlItems[controlItemId];
          
          if (controlItemData?.checks) {
            Object.keys(controlItemData.checks).forEach(checkId => {
              const checkData = controlItemData.checks[checkId];
              integrity.originalChecks++;
              
              // For cumulative format, only failed checks should be transformed
              if (checkData.isChecked) {
                // This check should have a corresponding field response
                const correspondingResponse = transformedData.find(response => 
                  response.category_id.toString() === categoryId.toString() &&
                  response.control_item_id.toString() === controlItemId.toString() &&
                  response.check_id.toString() === checkId.toString()
                );
                
                if (!correspondingResponse) {
                  integrity.missingTransformations++;
                  integrity.issues.push(`Missing transformation for failed check: category ${categoryId}, control item ${controlItemId}, check ${checkId}`);
                }
              }
            });
          }
        });
      }
    });
    
    // Check for unexpected transformations
    transformedData.forEach((response, index) => {
      // Verify this response corresponds to a failed check in original data
      const categoryData = originalData[response.category_id];
      const controlItemData = categoryData?.controlItems?.[response.control_item_id];
      const checkData = controlItemData?.checks?.[response.check_id];
      
      if (!checkData) {
        integrity.unexpectedTransformations++;
        integrity.issues.push(`Unexpected transformation at index ${index}: no corresponding check found`);
      } else if (!checkData.isChecked) {
        integrity.unexpectedTransformations++;
        integrity.issues.push(`Unexpected transformation at index ${index}: check was not failed`);
      }
    });
    
    integrity.dataConsistency = integrity.issues.length === 0;
    
    console.log(`📊 Integrity check: ${integrity.originalChecks} original checks, ${integrity.transformedResponses} transformed`);
    
    return integrity;
  }

  // Get validation recommendations
  getValidationRecommendations(validationResults) {
    const recommendations = [];
    
    if (validationResults.errorCount > 0) {
      recommendations.push({
        priority: 'high',
        type: 'errors',
        message: `${validationResults.errorCount} field responses have validation errors`,
        action: 'Review and fix validation errors before proceeding'
      });
    }
    
    if (validationResults.warningCount > validationResults.totalResponses * 0.1) {
      recommendations.push({
        priority: 'medium',
        type: 'warnings',
        message: `High number of validation warnings (${validationResults.warningCount})`,
        action: 'Review data quality and consider improving input data'
      });
    }
    
    if (validationResults.averageQuality < 70) {
      recommendations.push({
        priority: 'medium',
        type: 'quality',
        message: `Low average quality score (${validationResults.averageQuality})`,
        action: 'Improve data completeness and accuracy'
      });
    }
    
    if (validationResults.summary?.lowQuality > validationResults.totalResponses * 0.2) {
      recommendations.push({
        priority: 'low',
        type: 'improvement',
        message: 'Many field responses have low quality scores',
        action: 'Consider adding more detailed remarks and supporting data'
      });
    }
    
    return recommendations;
  }

  // Helper method to validate URL
  isValidUrl(string) {
    try {
      new URL(string);
      return true;
    } catch (_) {
      // Check for relative URLs or common URL patterns
      const urlPattern = /^(https?:\/\/)|(\/)/;
      return urlPattern.test(string);
    }
  }

  // Get validation statistics
  getStats() {
    const total = this.validationStats.totalValidations;
    
    return {
      ...this.validationStats,
      validationRate: total > 0 ? (this.validationStats.validResponses / total * 100).toFixed(2) + '%' : '0%',
      errorRate: total > 0 ? (this.validationStats.invalidResponses / total * 100).toFixed(2) + '%' : '0%',
      averageErrorsPerValidation: total > 0 ? (this.validationStats.errorsFound / total).toFixed(2) : '0',
      averageWarningsPerValidation: total > 0 ? (this.validationStats.warningsGenerated / total).toFixed(2) : '0'
    };
  }

  // Add custom validation rule
  addValidationRule(fieldName, rule) {
    if (typeof rule !== 'function') {
      throw new Error('Validation rule must be a function');
    }
    
    this.customRules = this.customRules || {};
    this.customRules[fieldName] = rule;
    
    console.log(`✅ Added custom validation rule for field: ${fieldName}`);
  }

  // Reset validation statistics
  resetStats() {
    this.validationStats = {
      totalValidations: 0,
      validResponses: 0,
      invalidResponses: 0,
      warningsGenerated: 0,
      errorsFound: 0
    };
    console.log('📊 Validation statistics reset');
  }

  // Export validator data
  export() {
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      validationRules: { ...this.validationRules },
      customRules: Object.keys(this.customRules || {})
    };
  }

  // Reset validator
  reset() {
    this.resetStats();
    this.customRules = {};
    console.log('🔄 Validator reset complete');
  }
}